/**
 * LoginPageWithRoles - WMS login page with role selection
 * 
 * Provides authentication interface with role-based access control.
 * Users can select their role (Admin, Manager, Operator, Client, Self-Service FBA)
 * before logging in. Integrates with backend authentication service and role management.
 * Redirects to role-specific dashboard after successful authentication.
 * 
 * Features:
 * - Role selection with visual indicators
 * - Username/password authentication
 * - Integration with useAuth and useRole contexts
 * - Automatic redirection based on selected role
 * - Real-time error handling and validation
 * - Fully internationalized with i18n support
 * 
 * @component
 * @returns {React.ReactElement} The rendered login page with role selection
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Login as LoginIcon,
  Warehouse as WarehouseIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  AdminPanelSettings as AdminIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext.helpers.js';
import { useRole, ROLES } from '../contexts/RoleContext.helpers.js';
import { useTranslation } from 'react-i18next';
import { API_ENDPOINTS } from '../config/api.config';

const LoginPageWithRoles = () => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, error, clearError } = useAuth();
  const { switchRole, ROLES: USER_ROLES } = useRole();

  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState(null);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from || '/admin/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location]);


  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear errors when user types
    if (error) {
      clearError();
    }
    if (localError) {
      setLocalError(null);
    }
  };

  // Login function
  const performLogin = async () => {
    if (!formData.username || !formData.password) {
      const errorMsg = t('auth.pleaseEnterCredentials', 'Please Enter Credentials');
      setLocalError(errorMsg);
      return;
    }

    setIsLoading(true);
    setLocalError(null);

    try {
      const result = await login({
        username: formData.username,
        password: formData.password
      });

      if (result && result.success) {
        const userRole = result.user?.role;
        const routes = {
          'client': '/client/dashboard',
          'manager': '/manager/dashboard',
          'admin': '/admin/dashboard',
          'operator': '/operator/dashboard',
          'selfservice': '/self-service',
          'fba': '/fba/orders'
        };
        const redirectTo = location.state?.from || routes[userRole] || '/admin/dashboard';
        
        if (userRole) {
          switchRole(userRole);
        }
        
        navigate(redirectTo, { replace: true });
      } else {
        const errorMsg = result?.error || t('auth.loginFailed', 'Login Failed');
        console.error('Login failed:', errorMsg);
        setLocalError(errorMsg);
      }
    } catch (err) {
      console.error('Login exception:', err);
      console.error('Error details:', { message: err.message, stack: err.stack });
      setLocalError(t('auth.connectionError', 'Connection Error'));
    } finally {
      setIsLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    await performLogin();
  };

  // Handle button click
  const handleButtonClick = async (e) => {
    e.preventDefault();
    await performLogin();
  };

  // Handle Enter key press
  const handleKeyPress = async (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      await performLogin();
    }
  };

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const displayError = localError || error;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        p: 2
      }}
    >
      <Card
        sx={{
          maxWidth: 450,
          width: '100%',
          boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
          borderRadius: 3,
          background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.1)'
        }}
      >
        <CardContent sx={{ p: 4 }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box sx={{ mb: 2 }}>
              <WarehouseIcon sx={{ fontSize: 48, color: '#00d4ff' }} />
            </Box>
            <Typography variant="h4" component="h1" sx={{ 
              fontWeight: 'bold', 
              mb: 1,
              background: 'linear-gradient(45deg, #00d4ff, #00ff88)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              DAOUXUN WMS
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)' }}>
              {t('auth.login.subtitle', 'Subtitle')}
            </Typography>
          </Box>

          <Divider sx={{ mb: 3, borderColor: 'rgba(255,255,255,0.1)' }} />

          {/* Error display */}
          {displayError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {displayError}
            </Alert>
          )}

          {/* Login form */}
          <form onSubmit={handleSubmit}>
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                id="username"
                label={t('auth.username', 'Username')}
                name="username"
                value={formData.username}
                onChange={handleChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    performLogin();
                  }
                }}
                required
                variant="outlined"
                autoComplete="username"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    '& fieldset': {
                      borderColor: 'rgba(255,255,255,0.3)'
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255,255,255,0.5)'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#00d4ff'
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255,255,255,0.7)'
                  },
                  '& .MuiOutlinedInput-input': {
                    color: 'white'
                  }
                }}
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                id="password"
                label={t('auth.password', 'Password')}
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    performLogin();
                  }
                }}
                required
                variant="outlined"
                autoComplete="current-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={togglePasswordVisibility}
                        edge="end"
                        sx={{ color: 'rgba(255,255,255,0.7)' }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'rgba(255,255,255,0.05)',
                    '& fieldset': {
                      borderColor: 'rgba(255,255,255,0.3)'
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(255,255,255,0.5)'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#00d4ff'
                    }
                  },
                  '& .MuiInputLabel-root': {
                    color: 'rgba(255,255,255,0.7)'
                  },
                  '& .MuiOutlinedInput-input': {
                    color: 'white'
                  }
                }}
              />
            </Box>

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleButtonClick}
              disabled={isLoading}
              startIcon={isLoading ? <CircularProgress size={20} /> : <LoginIcon />}
              sx={{
                py: 1.5,
                background: 'linear-gradient(45deg, #00d4ff, #00ff88)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #00b8e6, #00e67a)'
                },
                '&:disabled': {
                  background: 'rgba(255,255,255,0.1)'
                }
              }}
            >
              {isLoading ? t('auth.login.loggingIn', 'Logging In') : t('auth.login.loginButton')}
            </Button>
          </form>


          {/* Version */}
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
              Version 2.0.0 - © 2025 DAOUXUN
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default React.memo(LoginPageWithRoles);

